import React, { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { createEvaluator, validateEvaluatorEmail } from '@/services/evaluator';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';

interface EvaluatorFormProps {
  initialData?: {
    firstName: string;
    lastName: string;
    email: string;
    id?: string;
  };
  onSubmit: (data: { firstName: string; lastName: string; email: string }) => void;
  onCancel: () => void;
}

export default function EvaluatorForm({ 
  initialData, 
  onSubmit, 
  onCancel 
}: EvaluatorFormProps) {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    confirmEmail: initialData?.email || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);

  useEffect(() => {
    validateForm();
  }, [formData]);

  const validateForm = async () => {
    const newErrors: Record<string, string> = {};
    let valid = true;

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      valid = false;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      valid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
      valid = false;
    } else if (!initialData && currentUser) {
      setIsValidatingEmail(true);
      try {
        const { isValid: emailValid, message } = await validateEvaluatorEmail(
          currentUser.uid,
          formData.email
        );
        if (!emailValid) {
          newErrors.email = message || 'Invalid email';
          valid = false;
        }
      } catch (error) {
        console.error('Error validating email:', error);
      } finally {
        setIsValidatingEmail(false);
      }
    }

    // Only validate confirm email if this is a new evaluator
    if (!initialData) {
      if (!formData.confirmEmail.trim()) {
        newErrors.confirmEmail = 'Please confirm email';
        valid = false;
      } else if (formData.email !== formData.confirmEmail) {
        newErrors.confirmEmail = 'Emails do not match';
        valid = false;
      }
    }

    setErrors(newErrors);
    setIsValid(valid);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !currentUser || isSubmitting || isValidatingEmail) return;

    setIsSubmitting(true);
    try {
      const { confirmEmail, ...submitData } = formData;
      
      if (!initialData) {
        // Create new evaluator
        await createEvaluator(currentUser.uid, submitData);
      }
      
      onSubmit(submitData);
    } catch (error: any) {
      console.error('Error saving evaluator:', error);
      toast.error(error.message || 'Failed to save evaluator');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
              errors.firstName 
                ? 'border-red-300 focus:ring-red-500' 
                : 'border-gray-300 focus:ring-primary'
            }`}
            disabled={isSubmitting}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
              errors.lastName 
                ? 'border-red-300 focus:ring-red-500' 
                : 'border-gray-300 focus:ring-primary'
            }`}
            disabled={isSubmitting}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          disabled={!!initialData || isSubmitting}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
            errors.email 
              ? 'border-red-300 focus:ring-red-500' 
              : 'border-gray-300 focus:ring-primary'
          } ${initialData ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
        )}
      </div>

      {!initialData && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Email Address
          </label>
          <input
            type="email"
            name="confirmEmail"
            value={formData.confirmEmail}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
              errors.confirmEmail 
                ? 'border-red-300 focus:ring-red-500' 
                : 'border-gray-300 focus:ring-primary'
            }`}
            disabled={isSubmitting}
          />
          {errors.confirmEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.confirmEmail}</p>
          )}
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 hover:text-gray-900"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>
        <button
          type="submit"
          disabled={!isValid || isSubmitting || isValidatingEmail}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-dark rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Check className="w-5 h-5" />
          <span>
            {isSubmitting ? 'Saving...' : isValidatingEmail ? 'Validating...' : initialData ? 'Update' : 'Add'} Evaluator
          </span>
        </button>
      </div>
    </form>
  );
}